/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';

import './ProfileResidence.scss';

import { ConnectedGrayPrivacyCard } from '../../../components/card/PrivacyCard';
import { ScrollAwareScroller } from '../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../components/appNav/AppHeaderScrollable';
import ContentWrapper from '../../../components/ContentWrapper';

import {
  ResidenceCountry,
  ListItem,
  LocalizedListItem,
} from '../../../../apiServices/ListsService';
import { ProfileData } from '../../../../models/ProfileModel';
import { selectors as listSelectors } from '../../../redux/ducks/lists/lists';
import { PolicyName } from '../../../../models/PolicyModel';
import { Card, CardRowUI } from '../../../../bphComponents/bundle';
import COLORS from '../../../styles/Colors';

const US_CODE = 'USA';

interface Props {
  title:  {
    id: string;
    values?: object;
  };
  countries: ResidenceCountry[];
  states: ListItem[];
  profileData: ProfileData;
  onChange(profileData: ProfileData): void;
  onWithdrawConsent(): void;
  intl: any;
}

interface State {
  isCountryExpanded: boolean;
  isStatesExpanded: boolean;
}

@connect((state) => ({
  countries: listSelectors.getResidenceCountryCollection(state),
  states: listSelectors.getResidenceStateCollection(state),
}))
class ProfileResidence extends React.Component<Props, State> {

  state: State = {
    isCountryExpanded: !this.props.profileData.residence_country_id,
    isStatesExpanded: false,
  };

  render() {
    const { title, profileData: { residence_country_id, residence_state_id }, intl, onWithdrawConsent } = this.props;
    const countries = LocalizedListItem.wrap(this.props.countries, intl);
    const states = LocalizedListItem.wrap(this.props.states, intl);
    const selectedCountry = residence_country_id && countries.filter(c => c.item.id === residence_country_id)[0];
    const isUSA = residence_country_id && selectedCountry.item.code === US_CODE;
    const selectedUsaState = residence_state_id && states.filter(s => s.item.id === residence_state_id)[0];

    const sortedCountries = countries
      .sort((o1, o2) => {
        if (o1.item.code === US_CODE) {
          return -1;
        }
        if (o2.item.code === US_CODE) {
          return 1;
        }
        return o1.name.localeCompare(o2.name);
      });

    const sortedState = states
      .sort((o1, o2) => {
        return o1.name.localeCompare(o2.name);
      });

    return (
      <ScrollAwareScroller>
        <AppHeaderScrollableText {...title} />

        <ContentWrapper>

          <ConnectedGrayPrivacyCard
            name={PolicyName.RESIDENCE}
            subTitle="privacy.privacyBlock.residence.teaser"
            onWithdrawConsent={onWithdrawConsent}
          />

          <Card.CardShape>
            <Card.CollapsibleSummaryWithControl
              label={(
                <FormattedMessage id="profileResidence.country.field.label" />
              )}
              background={COLORS.gsLightest}
              disableCollapseUntilValue={false}
              hasBottomBorder={true}
              value={selectedCountry && <div className="font-color-profile-accent">{selectedCountry.name}</div>}
              isExpanded={this.state.isCountryExpanded}
              isMultiLine={false}
              onClick={() => {
                this.setState({
                  isCountryExpanded: !this.state.isCountryExpanded,
                });
              }}
            >
              {
                sortedCountries.map(country => {
                  return (
                    <CardRowUI.SelectableRow
                      key={country.item.id}
                      isSelected={country.item.id === residence_country_id}
                      selectedClassName="fill-color-profile-highlight"
                      hasBottomBorder={true}
                      onClick={() => {
                        this.setState({
                          isCountryExpanded: false,
                          isStatesExpanded: !isUSA && country.item.code === US_CODE,
                        });

                        this.updateResidence({
                          residence_country_id: country.item.id,
                          residence_state_id: (country.item.code === US_CODE ? residence_state_id : null),
                        });
                      }}
                    >
                      {country.name}
                    </CardRowUI.SelectableRow>
                  );
                })
              }
            </Card.CollapsibleSummaryWithControl>
          </Card.CardShape>

          {
            isUSA && (
              <Card.CardShape>
                <Card.CollapsibleSummaryWithControl
                  label={(
                    <FormattedMessage id="profileResidence.state.field.label" />
                  )}
                  background={COLORS.gsLightest}
                  disableCollapseUntilValue={false}
                  hasBottomBorder={true}
                  value={residence_state_id ? <div className="font-color-profile-accent">{selectedUsaState.item.name}</div> : null}
                  isExpanded={this.state.isStatesExpanded}
                  isMultiLine={false}
                  onClick={() => {
                    this.setState({
                      isStatesExpanded: !this.state.isStatesExpanded,
                    });
                  }}
                >
                  {
                    sortedState.map((state: LocalizedListItem<ListItem>) => (
                      <CardRowUI.SelectableRow
                        key={state.item.id}
                        isSelected={state.item.id === residence_state_id}
                        selectedClassName="fill-color-profile-highlight"
                        hasBottomBorder={true}
                        onClick={() => {
                          this.setState({ isStatesExpanded: false });
                          this.updateResidence({
                            residence_country_id: selectedCountry.item.id,
                            residence_state_id: state.item.id,
                          });
                        }}
                      >
                        {state.name}
                      </CardRowUI.SelectableRow>
                    ))
                  }
                </Card.CollapsibleSummaryWithControl>
              </Card.CardShape>
            )
          }

        </ContentWrapper>

      </ScrollAwareScroller>
    );
  }

  updateResidence(update: Partial<ProfileData>) {
    const { profileData, onChange } = this.props;

    onChange({
      ...profileData,
      ...update,
    });
  }

}

export default injectIntl(ProfileResidence);
